import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { getMySubscription, switchMySubscription } from '../../services/studentSubscription';
import { FEATURE_MENU_ITEMS } from '../StudentShell/StudentSidebar';

// Self-service subscription view + switch (docs/DECISIONS.md, "Default
// Trial Plan on signup + self-service subscription + registration consent"
// plan file) - fills SettingsModal.jsx's pre-existing 'subscriptions' tab
// slot. Free plans (price 0) are one-click self-switch; any other active
// plan is shown read-only with "Contact us to upgrade" - there's no payment
// gateway yet, so self-assigning a paid plan isn't offered here.

function featureLabel(key) {
  return FEATURE_MENU_ITEMS.find((item) => item.key === key)?.label || key;
}

function CurrentPlanCard({ current }) {
  if (!current) {
    return (
      <div className="rounded-card border border-gray-200 p-4 sm:p-5">
        <p className="text-sm font-semibold text-navy">No active subscription</p>
        <p className="text-xs text-text-slate mt-0.5">Pick a free plan below to get started.</p>
      </div>
    );
  }
  return (
    <div className="rounded-card border border-brand-blue/30 bg-brand-blue/5 p-4 sm:p-5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-navy">{current.plan_name}</p>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            current.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-text-slate'
          }`}
        >
          {current.is_active ? 'Active' : current.status}
        </span>
      </div>
      {current.daily_question_limit != null && (
        <p className="text-xs text-text-slate">Daily question limit: {current.daily_question_limit}</p>
      )}
      {current.end_date && <p className="text-xs text-text-slate">Renews/expires: {current.end_date}</p>}
      {current.allowed_features?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {current.allowed_features.map((key) => (
            <span key={key} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs text-text-slate border border-gray-200">
              <CheckCircle2 size={12} className="text-brand-blue" /> {featureLabel(key)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FreePlanRow({ plan, isCurrent, onSwitch, switching }) {
  return (
    <div className="rounded-card border border-gray-200 p-4 flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-navy flex items-center gap-1.5">
          <Sparkles size={14} className="text-brand-blue" /> {plan.name}
        </p>
        {plan.description && <p className="text-xs text-text-slate mt-0.5">{plan.description}</p>}
      </div>
      {isCurrent ? (
        <span className="text-xs font-semibold text-brand-blue flex-shrink-0">Current plan</span>
      ) : (
        <button
          type="button"
          onClick={() => onSwitch(plan)}
          disabled={switching}
          className="flex-shrink-0 px-3 py-1.5 rounded-btn text-xs font-semibold bg-brand-blue text-white hover:bg-brand-blue/90 disabled:opacity-60 transition-colors"
        >
          {switching ? 'Switching...' : 'Switch to this plan'}
        </button>
      )}
    </div>
  );
}

function OtherPlanRow({ plan }) {
  return (
    <div className="rounded-card border border-gray-200 p-4 flex items-center justify-between gap-3 opacity-90">
      <div>
        <p className="text-sm font-semibold text-navy">{plan.name}</p>
        {plan.description && <p className="text-xs text-text-slate mt-0.5">{plan.description}</p>}
      </div>
      <span className="flex-shrink-0 text-xs font-semibold text-text-slate">Contact us to upgrade</span>
    </div>
  );
}

export default function SubscriptionSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switchingPlanId, setSwitchingPlanId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setData(await getMySubscription());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSwitch = async (plan) => {
    setError('');
    setSwitchingPlanId(plan.plan_id);
    try {
      const response = await switchMySubscription(plan.plan_id);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to switch plan.');
      }
      await load();
    } catch (err) {
      setError(err.message || 'Failed to switch plan.');
    } finally {
      setSwitchingPlanId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-text-slate">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="text-sm text-text-slate">Couldn't load your subscription right now - try again shortly.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <CurrentPlanCard current={data.current} />

      {error && (
        <div className="rounded-card border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</div>
      )}

      {data.free_plans.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-slate">Free plans</p>
          {data.free_plans.map((plan) => (
            <FreePlanRow
              key={plan.plan_id}
              plan={plan}
              isCurrent={data.current?.plan_id === plan.plan_id}
              onSwitch={handleSwitch}
              switching={switchingPlanId === plan.plan_id}
            />
          ))}
        </div>
      )}

      {data.other_plans.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-slate">Other plans</p>
          {data.other_plans.map((plan) => (
            <OtherPlanRow key={plan.plan_id} plan={plan} />
          ))}
        </div>
      )}
    </div>
  );
}
